import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { TOOLS, getTool, type ToolConfig } from "../lib/atomika-tools";
import {
  toolStatusById,
  useOverview,
  type OverviewToolStatus,
} from "../lib/atomika-overview";
import { canAccessArea, useAuth } from "../lib/auth";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  const navigate = useNavigate();
  const overview = useOverview();
  const [layout, setLayout] = useState<"map" | "grid">("map");
  const isMap = layout === "map";

  return (
    <div className="mx-auto max-w-[1240px] px-7 pb-[60px] pt-[34px]">
      {/* Header row */}
      <div className="mb-[26px] flex flex-wrap items-start gap-6">
        <div className="min-w-[320px] flex-1">
          <div className="mb-2 text-[13px] font-bold uppercase tracking-[0.06em] text-brand">
            Atomika húsið
          </div>
          <h1 className="m-0 mb-2.5 font-display text-[38px] font-extrabold leading-[1.08] tracking-tight text-ink">
            Góðan dag{overview ? `, ${overview.user.name}` : ""}
          </h1>
          <p className="m-0 max-w-[560px] text-base leading-[1.5] text-muted">
            Sex tengdar lausnir fyrir sölu, rauntímarekstur, orkuspár,
            markaðssamskipti, sölupípu og vaktir — eitt vistkerfi, sameiginleg
            gögn.
          </p>
          <div className="mt-4 flex gap-2.5">
            <div className="inline-flex rounded-[11px] bg-[#E8F1FF] p-1">
              <button
                onClick={() => setLayout("map")}
                className="h-[34px] cursor-pointer rounded-lg px-[15px] text-[13px] font-bold transition-colors"
                style={
                  isMap
                    ? {
                        background: "#fff",
                        color: "#0B2447",
                        boxShadow: "0 2px 6px rgba(11,36,71,.12)",
                      }
                    : { background: "transparent", color: "#5B6B85" }
                }
              >
                Suite map
              </button>
              <button
                onClick={() => setLayout("grid")}
                className="h-[34px] cursor-pointer rounded-lg px-[15px] text-[13px] font-bold transition-colors"
                style={
                  !isMap
                    ? {
                        background: "#fff",
                        color: "#0B2447",
                        boxShadow: "0 2px 6px rgba(11,36,71,.12)",
                      }
                    : { background: "transparent", color: "#5B6B85" }
                }
              >
                Herbergi
              </button>
            </div>
            <button
              onClick={() => navigate({ to: "/farsimi" })}
              className="flex h-[42px] cursor-pointer items-center gap-[7px] rounded-[11px] border border-line bg-white px-[15px] text-[13px] font-semibold text-muted"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1E7BFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="7" y="2" width="10" height="20" rx="2.5" />
                <path d="M11 18h2" />
              </svg>
              Farsímaútgáfa
            </button>
          </div>
        </div>

        {/* Shared status summary */}
        <div className="grid w-[400px] max-w-full grid-cols-2 gap-3">
          {(overview?.kpis ?? []).map((k) => (
            <div
              key={k.label}
              className="rounded-2xl border border-line bg-white px-[18px] py-4"
            >
              <div className="text-xs font-semibold text-muted-2">
                {k.label}
              </div>
              <div
                className="mt-1 font-display text-[30px] font-extrabold leading-[1.1]"
                style={{ color: k.valueColor ?? "#0B2447" }}
              >
                {k.value}
                {k.unit && <span className="text-[17px]">{k.unit}</span>}
              </div>
              <div
                className="mt-0.5 text-xs font-semibold"
                style={{ color: k.subColor }}
              >
                {k.sub}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isMap ? <SuiteMap /> : <RoomsGrid />}

      {/* Bottom: shared activity */}
      <div className="mt-5 grid grid-cols-[1.3fr_1fr_1fr] gap-[18px]">
        <div className="rounded-[18px] border border-line bg-white px-[22px] py-5">
          <div className="mb-3.5 font-display text-[15px] font-bold text-ink">
            Nýleg virkni
          </div>
          {(overview?.activity ?? []).map((a, i) => (
            <div
              key={i}
              className="flex gap-3 border-b border-[#F1F5FB] py-[9px]"
            >
              <span
                className="mt-1.5 h-2 w-2 flex-none rounded-full"
                style={{ background: a.dot }}
              />
              <div className="flex-1">
                <div className="text-[13.5px] font-semibold leading-[1.4] text-ink">
                  {a.text}
                </div>
                <div className="mt-px text-[11.5px] text-muted-3">
                  {a.app} · {a.time}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-[18px] border border-line bg-white px-[22px] py-5">
          <div className="mb-3.5 font-display text-[15px] font-bold text-ink">
            Þarfnast athygli
          </div>
          {(overview?.tasks ?? []).map((t, i) => (
            <div
              key={i}
              className="mb-[9px] flex items-start gap-[11px] rounded-xl px-3 py-2.5"
              style={{ background: t.bg }}
            >
              <span
                className="mt-[5px] h-[9px] w-[9px] flex-none rounded-full"
                style={{ background: t.dot }}
              />
              <div className="flex-1">
                <div className="text-[13px] font-bold leading-[1.35] text-ink">
                  {t.text}
                </div>
                <div className="mt-px text-[11.5px] text-[#7B8AA0]">
                  {t.meta}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-[18px] border border-line bg-white px-[22px] py-5">
          <div className="mb-3.5 font-display text-[15px] font-bold text-ink">
            Kerfisstaða
          </div>
          {(overview?.systems ?? []).map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 border-b border-[#F1F5FB] py-[9px]"
            >
              <span
                className="h-2 w-2 flex-none rounded-full"
                style={{ background: s.dot }}
              />
              <div className="flex-1 text-[13px] font-semibold text-ink">
                {s.name}
              </div>
              <div className="text-xs font-bold" style={{ color: s.color }}>
                {s.val}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type HouseNode = { id: string; left: number; top: number; primary: boolean };

const HOUSE_NODES: HouseNode[] = [
  { id: "atomika-reiknivel", left: 73, top: 255, primary: true },
  { id: "markadurinn", left: 440, top: 255, primary: false },
  { id: "atomika-maelabord", left: 807, top: 255, primary: true },
  { id: "spalikan-orkunotkun", left: 73, top: 545, primary: false },
  { id: "vaktaskra", left: 440, top: 545, primary: false },
  { id: "atomika-heimasida", left: 807, top: 545, primary: false },
];

function SuiteMap() {
  const overview = useOverview();
  return (
    <div className="overflow-auto rounded-[22px] border border-line bg-white p-2 shadow-[0_12px_40px_rgba(11,36,71,.05)]">
      <svg
        viewBox="0 0 1180 810"
        className="mx-auto block h-auto w-full max-w-[1180px]"
      >
        <line
          x1="0"
          y1="778"
          x2="1180"
          y2="778"
          stroke="#DCE6F5"
          strokeWidth="2.5"
        />
        <rect
          x="40"
          y="190"
          width="1100"
          height="555"
          fill="#FDFEFF"
          stroke="#0B2447"
          strokeWidth="3"
        />
        <path
          d="M 20 195 L 590 28 L 1160 195"
          fill="#EAF2FF"
          stroke="#0B2447"
          strokeWidth="3"
          strokeLinejoin="round"
        />

        <rect
          x="41.5"
          y="191.5"
          width="730"
          height="282"
          fill="#EAF2FF"
          opacity="0.55"
        />
        <rect
          x="774.5"
          y="191.5"
          width="364"
          height="282"
          fill="#E7F7EF"
          opacity="0.6"
        />
        <rect
          x="41.5"
          y="476.5"
          width="364"
          height="267"
          fill="#F3F6FB"
          opacity="0.8"
        />
        <rect
          x="407.5"
          y="476.5"
          width="364"
          height="267"
          fill="#ECFDF5"
          opacity="0.7"
        />
        <rect
          x="774.5"
          y="476.5"
          width="364"
          height="267"
          fill="#E4F6F8"
          opacity="0.7"
        />

        <line
          x1="40"
          y1="475"
          x2="742"
          y2="475"
          stroke="#0B2447"
          strokeWidth="2.5"
        />
        <line
          x1="778"
          y1="475"
          x2="1140"
          y2="475"
          stroke="#0B2447"
          strokeWidth="2.5"
        />

        <foreignObject x="56" y="202" width="360" height="26">
          <div>
            <span
              style={{
                background: "#1E7BFF",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 800,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                padding: "4px 11px",
                borderRadius: "20px",
              }}
            >
              Svæði sölumanna
            </span>
          </div>
        </foreignObject>
        <foreignObject x="789" y="200" width="340" height="42">
          <div>
            <img
              src="/uploads/gglagnir.is_logo.png"
              alt="GG Lagnir"
              style={{
                display: "inline-block",
                height: "30px",
                background: "#fff",
                padding: "5px 10px",
                borderRadius: "10px",
                border: "1px solid #C4EBD5",
              }}
            />
          </div>
        </foreignObject>
        <foreignObject x="56" y="486" width="200" height="26">
          <div>
            <span
              style={{
                background: "#7B8AA0",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 800,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                padding: "4px 11px",
                borderRadius: "20px",
              }}
            >
              Eyja
            </span>
          </div>
        </foreignObject>
        <foreignObject x="422" y="486" width="200" height="26">
          <div>
            <span
              style={{
                background: "#0F766E",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 800,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                padding: "4px 11px",
                borderRadius: "20px",
              }}
            >
              Rekstur
            </span>
          </div>
        </foreignObject>
        <foreignObject x="1050" y="486" width="120" height="26">
          <div>
            <span
              style={{
                background: "#7B8AA0",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 800,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                padding: "4px 11px",
                borderRadius: "20px",
              }}
            >
              Eyja
            </span>
          </div>
        </foreignObject>

        <line
          x1="406"
          y1="190"
          x2="406"
          y2="310"
          stroke="#0B2447"
          strokeWidth="2.5"
        />
        <line
          x1="406"
          y1="365"
          x2="406"
          y2="600"
          stroke="#0B2447"
          strokeWidth="2.5"
        />
        <line
          x1="406"
          y1="655"
          x2="406"
          y2="745"
          stroke="#0B2447"
          strokeWidth="2.5"
        />
        <line
          x1="773"
          y1="190"
          x2="773"
          y2="310"
          stroke="#0B2447"
          strokeWidth="3"
        />
        <line
          x1="773"
          y1="365"
          x2="773"
          y2="475"
          stroke="#0B2447"
          strokeWidth="3"
        />
        <line
          x1="773"
          y1="500"
          x2="773"
          y2="600"
          stroke="#0B2447"
          strokeWidth="3"
        />
        <line
          x1="773"
          y1="655"
          x2="773"
          y2="745"
          stroke="#0B2447"
          strokeWidth="3"
        />
        <rect x="555" y="743.5" width="70" height="5" fill="#FDFEFF" />
        <rect x="562" y="742" width="56" height="36" rx="4" fill="#0B2447" />
        <circle cx="608" cy="761" r="3.5" fill="#9DC0F5" />

        <foreignObject x="440" y="88" width="300" height="95">
          <div
            style={{
              width: "300px",
              height: "95px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <img
              src="/uploads/atomika-mark-blue.png"
              alt="Atomika"
              style={{ height: "34px", marginBottom: "6px" }}
            />
            <div
              style={{
                fontFamily: "'Sora', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: "16px",
                color: "#0B2447",
              }}
            >
              Atomika húsið
            </div>
            <div
              style={{ fontSize: "11.5px", color: "#5B6B85", fontWeight: 600 }}
            >
              Sex herbergi · eitt vistkerfi
            </div>
          </div>
        </foreignObject>

        <g fill="#1E7BFF">
          <line
            x1="380"
            y1="338"
            x2="424"
            y2="338"
            stroke="#1E7BFF"
            strokeWidth="2.6"
          />
          <polygon points="422,331 440,338 422,345" />
          <path
            d="M 880 538 C 880 490 760 512 760 450"
            fill="none"
            stroke="#1E7BFF"
            strokeWidth="2.6"
            strokeDasharray="7 8"
            style={{ animation: "flowDash 1s linear infinite" }}
          />
          <polygon points="752,452 760,432 768,452" />
          <circle cx="880" cy="538" r="5" fill="#1E7BFF" />
          <line
            x1="762"
            y1="338"
            x2="790"
            y2="338"
            stroke="#1E7BFF"
            strokeWidth="2.6"
          />
          <polygon points="790,331 808,338 790,345" />
          <polygon points="762,331 744,338 762,345" />
        </g>

        <foreignObject x="316" y="435" width="180" height="26">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span style={flowLabel("#EAF2FF", "#1E7BFF", "#CFE0FB")}>
              tilboð · verð · sparnaður
            </span>
          </div>
        </foreignObject>
        <foreignObject x="686" y="435" width="180" height="26">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span style={flowLabel("#E7F7EF", "#16A75C", "#C4EBD5")}>
              samningar ↔ rauntímastaða
            </span>
          </div>
        </foreignObject>
        <foreignObject x="130" y="486" width="260" height="26">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span style={flowLabel("#EEF2F7", "#7B8AA0", "#E1E8F1")}>
              ekkert gagnaflæði
            </span>
          </div>
        </foreignObject>
        <foreignObject x="545" y="486" width="210" height="26">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span style={flowLabel("#ECFDF5", "#0F766E", "#A7F3D0")}>
              frávik → á vakt
            </span>
          </div>
        </foreignObject>
        <foreignObject x="790" y="486" width="250" height="26">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <span style={flowLabel("#EAF2FF", "#1E7BFF", "#CFE0FB")}>
              fyrirspurnir af vef → CRM
            </span>
          </div>
        </foreignObject>

        {HOUSE_NODES.map((n) => {
          const tool = getTool(n.id);
          if (!tool) return null;
          return (
            <foreignObject
              key={n.id}
              x={n.left}
              y={n.top}
              width="300"
              height="170"
            >
              <MapNodeCard
                tool={tool}
                status={toolStatusById(overview, n.id)}
                primary={n.primary}
              />
            </foreignObject>
          );
        })}
      </svg>
    </div>
  );
}

function flowLabel(
  bg: string,
  color: string,
  border: string,
): React.CSSProperties {
  return {
    background: bg,
    color,
    fontSize: "11px",
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: "20px",
    border: `1px solid ${border}`,
    whiteSpace: "nowrap",
  };
}

function MapNodeCard({
  tool,
  status,
  primary,
}: {
  tool: ToolConfig;
  status?: OverviewToolStatus;
  primary?: boolean;
}) {
  return (
    <div
      style={{
        width: "300px",
        height: "170px",
        background: "#fff",
        border: "1px solid #E5EDF8",
        borderRadius: "18px",
        padding: "16px 18px",
        boxShadow: "0 8px 26px rgba(11,36,71,.07)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "12px",
            background: tool.bg,
            color: tool.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Sora', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: "18px",
            flex: "none",
          }}
        >
          {tool.glyph}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "'Sora', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: "15px",
              color: "#0B2447",
            }}
          >
            {tool.name}
          </div>
          <div style={{ fontSize: "11.5px", color: "#8A98AE" }}>{tool.tag}</div>
        </div>
        {status && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "10.5px",
              fontWeight: 700,
              color: status.statusColor,
              background: status.statusBg,
              padding: "4px 8px",
              borderRadius: "20px",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: status.statusDot,
              }}
            />
            {status.statusLabel}
          </span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: "18px",
          marginTop: "14px",
          alignItems: "flex-end",
        }}
      >
        <div>
          <div style={statValue("#0B2447")}>{status?.k1v ?? "—"}</div>
          <div style={statLabel}>{status?.k1l ?? ""}</div>
        </div>
        <div>
          <div style={statValue(status?.k2Color ?? "#0B2447")}>
            {status?.k2v ?? "—"}
          </div>
          <div style={statLabel}>{status?.k2l ?? ""}</div>
        </div>
        <div style={{ flex: 1 }} />
        <Link
          to={tool.route}
          style={{
            height: "36px",
            padding: "0 13px",
            border: primary ? "none" : "1px solid #CFE0FB",
            borderRadius: "10px",
            background: primary ? "#1E7BFF" : "#fff",
            color: primary ? "#fff" : "#1E7BFF",
            fontWeight: 700,
            fontSize: "12.5px",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Opna lausn
        </Link>
      </div>
    </div>
  );
}

const statLabel: React.CSSProperties = {
  fontSize: "11px",
  color: "#8A98AE",
  fontWeight: 600,
};

function statValue(color: string): React.CSSProperties {
  return {
    fontFamily: "'Sora', system-ui, sans-serif",
    fontWeight: 800,
    fontSize: "20px",
    color,
  };
}

function RoomsGrid() {
  const overview = useOverview();
  const { user } = useAuth();
  return (
    <div className="grid grid-cols-2 gap-[18px] xl:grid-cols-3">
      {TOOLS.filter((t) => canAccessArea(user, t.id)).map((t) => {
        const status = toolStatusById(overview, t.id);
        return (
          <div
            key={t.id}
            className="flex min-h-[236px] flex-col rounded-[20px] border border-line bg-white p-6 shadow-[0_10px_30px_rgba(11,36,71,.05)]"
          >
            <div className="flex items-start gap-3.5">
              <div
                className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-[14px] font-display text-[23px] font-extrabold"
                style={{ background: t.bg, color: t.color }}
              >
                {t.glyph}
              </div>
              <div className="flex-1">
                <div className="font-display text-[19px] font-bold text-ink">
                  {t.name}
                </div>
                <div className="text-[13px] font-semibold text-muted-2">
                  {t.tag}
                </div>
              </div>
              {status && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-[20px] px-[11px] py-[5px] text-xs font-bold"
                  style={{
                    color: status.statusColor,
                    background: status.statusBg,
                  }}
                >
                  <span
                    className="h-[7px] w-[7px] rounded-full"
                    style={{ background: status.statusDot }}
                  />
                  {status.statusLabel}
                </span>
              )}
            </div>
            <p className="m-0 mt-3.5 text-sm leading-[1.5] text-muted">
              {t.desc}
            </p>
            <div className="mt-auto flex gap-[26px] pt-[18px]">
              <div>
                <div className="font-display text-2xl font-extrabold text-ink">
                  {status?.k1v ?? "—"}
                </div>
                <div className="text-[11.5px] font-semibold text-muted-2">
                  {status?.k1l ?? ""}
                </div>
              </div>
              <div>
                <div className="font-display text-2xl font-extrabold text-ink">
                  {status?.k2v ?? "—"}
                </div>
                <div className="text-[11.5px] font-semibold text-muted-2">
                  {status?.k2l ?? ""}
                </div>
              </div>
              <div className="flex-1" />
              <Link
                to={t.route}
                className="flex h-[42px] items-center gap-2 self-end rounded-[11px] bg-brand px-5 text-sm font-bold text-white"
              >
                Opna lausn
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="mt-3 text-[11.5px] text-muted-4">
              Síðasta notkun: {status?.last ?? "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}
